import React, { useEffect, useState } from "react";
import {
  AlertColor,
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  Grid2,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Project, ShowModalProps } from "../../types/projectTypes";
import {
  createProject,
  getProjectById,
  getActiveProjects, // Usamos esto para cargar todos los activos si es admin
  getStatistics,
  updateProject,
  deleteProject,
} from "../../api/projectApi";
import ProjectForm from "../../components/ProjectForm";
import TowerTable from "../../components/TowerTable";
import { Tower } from "../../types/towerTypes";
import {
  deleteTower,
  downloadTowerFormat,
  exportTowerData,
  getTowersByProjectId,
  getTowerUsage,
  importTowerData,
  updateTower,
  TowerUsage,
} from "../../api/towerApi";
import { getProjectsUser, getUserProject, getUsers, ProjectsToUser } from "../../api/userApi";
import { User } from "../../types/userTypes";
import UserAssignmentTable from "../../components/UserAssignmentTable";
import { Snackbar, Alert } from "@mui/material";
import { ProjectStatistics } from "../../types/projectStatisticsType";
import ProjectActivityPage from "./ProjectActivityPage";
import { addPositionsSalary, getPositions } from "../../api/parametersApi";
import { Positions, PositionsSalary } from "../../types/EmployeeTypes";
import { ProjectActivity } from "../../types/ActivityTypes";
import { getProjectActivities, updateProjectActivitiesOrder } from "../../api/activityApi";

import Loader from "../../components/Loader";
import ModalComponent from '../../components/ErrorsModal';
import { profiles, ADMIN_SISTEMA } from "../../contexts/constants/profiles";
import { DELETE_CONCURRENCY } from "../../contexts/constants/bulkDelete";

const Proyectos: React.FC = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [projectInput, setProjectInput] = useState("");
  const [idProject, setIdProject] = useState<string>("");
  const [newProjectName, setNewProjectName] = useState("");
  const [towersData, setTowerData] = useState<Tower[]>([]);
  // Torres que no se pueden eliminar, agrupadas por motivo.
  const [towerUsage, setTowerUsage] = useState<TowerUsage>({ withDailyReports: [], withActivityAssignments: [] });
  const [users, setUsers] = useState<User[]>([]);
  const [open, setOpen] = useState(false);
  const [openActivities, setOpenActivities] = useState(false);
  const [statisticsData, setStatisticsData] = useState<ProjectStatistics | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [alertType, setAlertType] = useState<AlertColor>('success');
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [positions, setPositions] = useState<Positions[]>([]);
  const [temporarySalaries, setTemporarySalaries] = useState<PositionsSalary[]>([]);
  const [activities, setActivities] = useState<ProjectActivity[]>([]);
  const [projectActivities, setProjectActivities] = useState<ProjectActivity[]>([]);

  const [isProjectSelected, setIsProjectSelected] = useState(false);
  const [tooltipText, setTooltipText] = useState("Debe seleccionar un proyecto para poder asignar.");
  const [loading, setLoading] = useState<boolean>(false);
  const [userRole, setUserRole] = useState<string>("");

  const [showModal, setShowModal] = useState<ShowModalProps>({
    show: false,
    message: "",
  });
  const [mostrarModal, setMostrarModal] = useState(false);
  const [mensajeError, setMensajeError] = useState('');

  // --- LÓGICA DE PERMISOS ---

  // 1. Permiso para CREAR y MODIFICAR: Director (0) y Gerente (2)
  const canCreateOrEdit = userRole === profiles[0].name || userRole === profiles[2].name || userRole === ADMIN_SISTEMA;

  // 2. Permiso para ELIMINAR: Gerente (2) y Administrador de Sistema (super-admin)
  const canDelete = userRole === profiles[2].name || userRole === ADMIN_SISTEMA;

  const mostrarError = (mensaje: string) => {
    setMensajeError(mensaje);
    setMostrarModal(true);
  };
  const cerrarModal = () => {
    setMostrarModal(false);
    setMensajeError('');
  };

  useEffect(() => {
    const getUserData = () => {
      let rut = '';
      let role = '';
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const parsedUser = JSON.parse(userStr);
        rut = parsedUser.identificationNum;
        role = parsedUser.rols?.description || "";
      }
      return { rut, role };
    }

    const { rut, role } = getUserData();
    setUserRole(role);

    const fetchProjects = async () => {
      try {
        setLoading(true);
        let data: Project[] = [];

        // Si tiene permiso de Crear/Editar (Director o Gerente), debe ver TODOS los activos
        if (role === profiles[0].name || role === profiles[2].name || role === ADMIN_SISTEMA) {
          data = await getActiveProjects();
        } else {
          // Visualizador u otros roles solo ven los asignados
          const userProjects = await getUserProject(rut);
          data = Array.isArray(userProjects) ? userProjects : [];
        }

        // Filtro adicional por seguridad (status true)
        const validProjects = data.filter(p => p && (p.status === undefined || p.status === true));
        setProjects(validProjects);
      } catch (error) {
        console.error("Error cargando proyectos:", error);
        setProjects([]);
      } finally {
        setLoading(false);
      }
    };

    fetchProjects();
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    const positionData = await getPositions();
    setPositions(positionData);

    try {
      const fetchedUsers = await getUsers();
      const userWithProjects = await Promise.all(
        fetchedUsers.map(async (user) => {
          const projectsResponse = await getProjectsUser(
            user.identificationNum
          );
          return { ...user, projects: projectsResponse };
        })
      );
      setUsers(userWithProjects);
    } catch (error) {
      console.error("Error al obtener los usuarios:", error);
    }
  };

  // Mapeo para cumplir con tipos estrictos si es necesario
  const simplifiedProjects = projects.map((project) => ({
    ...project,
    id: project.id,
    name: project.name,
    costCenter: project.costCenter,
    status: project.status ?? true
  }));

  const handleActivitiesUpdate = (newActivities: ProjectActivity[]) => {
    setActivities(newActivities);
  };

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleOpenActivities = () => {
    setOpenActivities(true);
  };

  const updateOrder = async (activities: ProjectActivity[]) => {
    try {
      await updateProjectActivitiesOrder(activities);
    } catch (error) {
      console.error("Error actualizar", error);
    }
  };

  const handleSaveActivities = () => {
    if (activities.length > 0) {
      updateOrder(activities);
      setSnackbarMessage("Orden de actividades actualizado");
      setSnackbarOpen(true);
      setAlertType("success");
    }
  };

  const handleCloseActivities = async () => {
    setOpenActivities(false);
    if (selectedProject?.id) {
      const acts = await getProjectActivities(selectedProject.id);
      setProjectActivities(acts);
    }
  };

  const selectedActivitiesForTable = projectActivities.filter(pa => pa.activityTower);

  const getTowers = async (projectId: number) => {
    const towers = await getTowersByProjectId(projectId);
    setTowerData(towers);
    // Se resuelve en una sola llamada qué torres no se pueden eliminar, para que la tabla
    // pueda bloquear el botón sin consultar torre por torre.
    setTowerUsage(await getTowerUsage(projectId));
    fetchStatisticsProject(projectId);
  };

  const handleProjectSelect = async (projectId: number | "") => {
    if (projectId !== "") {
      const selectedProject = await getProjectById(projectId);

      if (selectedProject) {
        setSelectedProject(selectedProject);
        setProjectInput(selectedProject.name || "");
        getTowers(projectId);
        const acts = await getProjectActivities(projectId);
        setProjectActivities(acts);
      }
    } else {
      setSelectedProject(null);
      setProjectActivities([]);
    }
  };

  const fetchStatisticsProject = async (projectId: number) => {
    try {
      const statisticsProject = await getStatistics(projectId);
      setStatisticsData(statisticsProject);
    } catch (error) {
      console.error("Error al obtener los datos externos", error);
    }
  };

  const handleEditTower = async (towerUpdate: Tower) => {
    await updateTower(towerUpdate.id!, towerUpdate);

    setTowerData(
      towersData.map((tower) =>
        tower.id === towerUpdate.id ? towerUpdate : tower
      )
    );

    if (selectedProject?.id) {
      await fetchStatisticsProject(selectedProject.id);
    }
  };

  const handleDeleteTower = async (id: number) => {
    try {
      await deleteTower(id);
      setTowerData(towersData.filter((tower) => tower.id !== id));
    } catch (error: any) {
      // El backend rechaza el borrado si la torre tiene reportes o asignaciones.
      const backendMessage = error.response?.data?.mensaje
        || error.response?.data?.message
        || 'No se pudo eliminar la torre.';
      setShowModal({ show: true, message: backendMessage });
      setMostrarModal(true);
    }
  };

  /**
   * Eliminación múltiple de torres. No hay endpoint masivo, así que se recorre la selección
   * llamando al endpoint individual, en tandas para no encolar una request por torre.
   *
   * La tabla ya excluye de la selección las torres bloqueadas; el manejo de error aquí es
   * la red de seguridad si el estado de uso quedó desactualizado.
   */
  const handleDeleteMultipleTowers = async (towerIds: number[]) => {
    const results: { id: number; error?: string }[] = [];

    for (let i = 0; i < towerIds.length; i += DELETE_CONCURRENCY) {
      const batch = towerIds.slice(i, i + DELETE_CONCURRENCY);

      const batchResults = await Promise.all(batch.map(async (id) => {
        const label = towersData.find(tower => tower.id === id)?.towerNumber || `ID ${id}`;
        try {
          await deleteTower(id);
          return { id };
        } catch (error: any) {
          console.error('Error al eliminar la torre', error);
          const backendMessage = error.response?.data?.mensaje
            || error.response?.data?.message
            || 'Error al eliminar';
          return { id, error: `${label}: ${backendMessage}` };
        }
      }));

      // Se acumula en el orden de la selección, no en el orden de respuesta del servidor.
      results.push(...batchResults);
    }

    const deletedIds = results.filter(r => !r.error).map(r => r.id);
    const errors = results.filter(r => r.error).map(r => r.error as string);

    if (deletedIds.length > 0) {
      setTowerData(prev => prev.filter(tower => tower.id == null || !deletedIds.includes(tower.id)));
      if (selectedProject?.id) {
        setTowerUsage(await getTowerUsage(selectedProject.id));
      }
    }

    const lines: string[] = [
      deletedIds.length === 1 ? 'Se eliminó 1 torre.' : `Se eliminaron ${deletedIds.length} torres.`
    ];
    if (errors.length > 0) {
      lines.push('');
      lines.push(errors.length === 1 ? 'No se pudo eliminar 1 torre:' : `No se pudieron eliminar ${errors.length} torres:`);
      errors.forEach(e => lines.push('• ' + e));
    }
    setShowModal({ show: true, message: lines.join('\n') });
    setMostrarModal(true);
  };

  const handleDeleteProject = async () => {
    if (!selectedProject || !selectedProject.id) return;

    if (window.confirm(`¿Está seguro que desea eliminar el proyecto "${selectedProject.name}"?`)) {
      try {
        setLoading(true);
        // Borrado lógico
        await deleteProject(selectedProject.id);

        // Actualizar UI
        setProjects(projects.filter(p => p.id !== selectedProject.id));
        setSelectedProject(null);
        setProjectInput("");
        setTowerData([]);
        setIsProjectSelected(false);

        setSnackbarMessage("Proyecto eliminado correctamente");
        setAlertType("success");
        setSnackbarOpen(true);
      } catch (error: any) {
        console.error("Error al eliminar proyecto:", error);
        setSnackbarMessage("Error al eliminar el proyecto");
        setAlertType("error");
        setSnackbarOpen(true);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleAutocompleteChange = (
    _: React.SyntheticEvent,
    newValue: string | null | undefined
  ) => {
    const selectedProject = projects.find(
      (project) => project.name === newValue
    );
    handleProjectSelect(selectedProject?.id || "");
    setIdProject(selectedProject?.id?.toString() || "");
    setNewProjectName(newValue || "");
    setProjectActivities([]);
    if (newValue) {
      setTooltipText("");
      setIsProjectSelected(true);
    } else {
      setTooltipText("Debe seleccionar un proyecto.");
      setIsProjectSelected(false);
    }
  };

  const handleCreateProject = () => {
    setIsCreating(true);
    setSelectedProject(null);
    setProjectInput("");
    setTowerData([]);
  };

  const handleCancelCreate = () => {
    setIsCreating(false);
    setProjectInput("");
    setSelectedProject(null);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleSaveProject = async (projectUpdated: Project) => {
    if (isCreating) {
      try { // <-- 1. Añadimos el try
        const createdProject = await createProject(projectUpdated);
        const newProjectWithStatus = { ...createdProject, status: true };

        try {
          const userStr = localStorage.getItem('user');
          if (userStr) {
            const currentUser = JSON.parse(userStr);
            await ProjectsToUser({
              idUserProject: 0,
              identificationNum: currentUser.identificationNum,
              project: createdProject
            });
          }
        } catch (error) {
          console.error("Error al asociar el proyecto al usuario creador:", error);
        }

        setProjects([...projects, newProjectWithStatus]);
        setSelectedProject(newProjectWithStatus);
        setProjectInput(newProjectWithStatus.name || "");
        setIsProjectSelected(true);
        setTooltipText("");

        if (temporarySalaries.length > 0) {
          const updatedSalaries = temporarySalaries.map((salary) => ({
            ...salary,
            projectId: createdProject,
          }));

          await Promise.all(
            updatedSalaries.map((salary) =>
              addPositionsSalary({
                salary: salary.salary ?? 0,
                projectId: salary.projectId.id ?? 0,
                positionId: salary.positionId?.id ?? 0,
                description: salary.positionId?.description ?? "",
              })
            )
          );

          setTemporarySalaries([]);
        }

        setSnackbarMessage("Proyecto creado con éxito");
        setSnackbarOpen(true);
        setAlertType("success");
        setIsCreating(false);

      } catch (error: any) {
        console.error("Error al crear el proyecto:", error);
        const errorMsg = error?.response?.data?.mensaje || error?.response?.data?.message || "Error al crear el proyecto. Verifica los datos.";
        setSnackbarMessage(errorMsg);
        setSnackbarOpen(true);
        setAlertType("error");
        return;
      }
    } else {
      try {
        await updateProject(projectUpdated);
        const updatedProjects = projects.map((p) =>
          p.id === projectUpdated.id ? { ...projectUpdated, status: p.status } : p
        );
        setProjects(updatedProjects);
        setSelectedProject(projectUpdated);
        setSnackbarMessage("Proyecto actualizado con éxito");
        setSnackbarOpen(true);
        setAlertType("success");
        setIsCreating(false);
      } catch (error: any) {
        setSnackbarMessage(error?.response?.data?.mensaje || "Error al actualizar el proyecto");
        setSnackbarOpen(true);
        setAlertType("error");
      }
    }

    if (projectUpdated.id) {
      fetchStatisticsProject(projectUpdated.id);
    }
  };

  const handleDownloadFormat = async () => {
      const targetId = selectedProject?.id?.toString();

      if (!targetId) {
        console.error("No hay proyecto seleccionado para descargar el formato");
        return;
      }

      try {
        setLoading(true);
        await downloadTowerFormat(targetId);
      } catch (error) {
        console.error("Error al descargar el formato:", error);
      } finally {
        setLoading(false);
      }
    };

 const handleImportTowers = async (file: File) => {
     const targetId = selectedProject?.id?.toString();

     if (!targetId) return;

     try {
       setLoading(true);
       await importTowerData(file, targetId);
       setSnackbarMessage("Se han importado correctamente todas las torres.");
       setAlertType("success");
       setSnackbarOpen(true);

     } catch (error: any) {
       console.error("Error al importar torres:", error);

       const errorMessage = error?.response?.data?.mensaje || error?.message || "Error inesperado.";

       setShowModal({ show: true, message: errorMessage });
       mostrarError(errorMessage);

     } finally {
       if (selectedProject?.id) {
         await getTowers(selectedProject.id);
       }
       setLoading(false);
     }
   };

    const handleExportTowers = async () => {
      const targetId = selectedProject?.id?.toString();

      if (!targetId) {
        console.error("No hay proyecto seleccionado para exportar");
        return;
      }

      try {
        setLoading(true);
        await exportTowerData(targetId);
      } catch (error) {
        console.error("Error al exportar torres:", error);
      } finally {
        setLoading(false);
      }
    };

  const onCloseShowModal = () => {
    setShowModal({ show: false, message: "" });
  };

  return (
    <Box sx={{ padding: 2 }}>
      {loading && <Loader />}

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={alertType}
          variant="filled"
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      <Grid container alignItems="end" justifyContent="space-between">
        <Grid item>
          <Box sx={{ display: "flex", gap: 2 }}>
            <Tooltip title={tooltipText} placement="top">
              <Button
                variant="contained"
                color="primary"
                onClick={handleOpen}
                disabled={!isProjectSelected}
              >
                Asignar Usuarios al Proyecto
              </Button>
            </Tooltip>

            <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
              <DialogContent>
                {selectedProject ? (
                  <Grid2>
                    <UserAssignmentTable
                      users={users}
                      project={selectedProject}
                    />
                    <DialogActions>
                      <Button onClick={handleClose} color="primary">
                        Cancelar
                      </Button>
                      <Button
                        onClick={handleClose}
                        color="primary"
                        variant="contained"
                      >
                        Guardar
                      </Button>
                    </DialogActions>
                  </Grid2>
                ) : (
                  <Grid2>
                    <Typography
                      variant="h6"
                      align="center"
                      color="textSecondary"
                    >
                      Debe seleccionar un proyecto antes de asignar usuarios.
                    </Typography>
                    <Grid2 container justifyContent="flex-end">
                      <Button onClick={handleClose} color="primary">
                        Cancelar
                      </Button>
                    </Grid2>
                  </Grid2>
                )}
              </DialogContent>
            </Dialog>
            <Tooltip title={tooltipText} placement="top">
              <Button
                variant="contained"
                color="primary"
                onClick={handleOpenActivities}
                disabled={!isProjectSelected}
              >
                Asignar Actividades al Proyecto
              </Button>
            </Tooltip>

            <Dialog
              open={openActivities}
              onClose={handleCloseActivities}
              fullWidth
              fullScreen
            >
              <DialogContent >
                {selectedProject ? (
                  <Grid2 >
                    <ProjectActivityPage
                      onReorder={handleActivitiesUpdate}
                      project={selectedProject}
                      onSaveClick={handleSaveActivities}
                      onCloseClick={handleCloseActivities}
                    />
                  </Grid2>
                ) : (
                  <Grid2>
                    <Typography
                      variant="h6"
                      align="center"
                      color="textSecondary"
                    >
                      Debe seleccionar un proyecto antes de asignar actividades.
                    </Typography>
                    <Grid2 container justifyContent="flex-end">
                      <Button onClick={handleCloseActivities} color="primary">
                        Cancelar
                      </Button>
                    </Grid2>
                  </Grid2>
                )}
              </DialogContent>
            </Dialog>

          </Box>
        </Grid>
      </Grid>

      {!isCreating && (
        <Grid container alignItems="center" spacing={2} sx={{ mt: 2 }}>
          <Grid item xs={8} md={6}>
            <Autocomplete
              options={projects.map((project) => project.name)}
              freeSolo
              inputValue={projectInput}
              onInputChange={(event, newInputValue) => {
                setProjectInput(newInputValue);
                if (!newInputValue) {
                  setIsProjectSelected(false);
                }
              }}
              onChange={handleAutocompleteChange}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Seleccionar o buscar proyecto"
                  variant="outlined"
                  fullWidth
                />
              )}
            />
          </Grid>

          {/* Botones controlados por PERMISOS */}
          <Grid item xs={4} md={6} sx={{ display: 'flex', gap: 2 }}>
            {/* CREAR: Director o Gerente */}
            {canCreateOrEdit && (
              <Button
                variant="contained"
                color="primary"
                onClick={handleCreateProject}
              >
                Crear Proyecto
              </Button>
            )}

            {/* ELIMINAR: EXCLUSIVO Gerente y si hay selección */}
            {canDelete && isProjectSelected && (
               <Button
               variant="contained"
               color="error"
               onClick={handleDeleteProject}
             >
               Eliminar Proyecto
             </Button>
            )}
          </Grid>
        </Grid>
      )}

      <Grid item xs={12} sx={{ mt: 4 }}>
        {isCreating ? (
          <>
            <ProjectForm
              isCreating={isCreating}
              setShowModal={setShowModal}
              onCancel={handleCancelCreate}
              project={null}
              onSave={handleSaveProject}
              statisticsProject={statisticsData}
              positions={positions}
              onSaveTemporarySalaries={(salaries) =>
                setTemporarySalaries(salaries)
              }
              setSnackbarOpen={setSnackbarOpen}
              setSnackbarMessage={setSnackbarMessage}
            />
          </>
        ) : (
          selectedProject && (
            <ProjectForm
              isCreating={isCreating}
              setShowModal={setShowModal}
              onCancel={handleCancelCreate}
              project={selectedProject}
              statisticsProject={statisticsData}

              // MODIFICAR: Solo Director o Gerente pueden guardar cambios
              onSave={canCreateOrEdit ? handleSaveProject : async () => {}}

              positions={positions}
              onSaveTemporarySalaries={(salaries) =>
                setTemporarySalaries(salaries)
              }
              setSnackbarOpen={setSnackbarOpen}
              setSnackbarMessage={setSnackbarMessage}
            />
          )
        )}
      </Grid>

      {(selectedProject || isCreating) && (
        <Grid item xs={12} sx={{ mt: 4 }}>
          <TowerTable
            onDeleteTower={handleDeleteTower}
            onDeleteMultipleTowers={handleDeleteMultipleTowers}
            towerUsage={towerUsage}
            onEditTower={handleEditTower}
            towers={towersData || []}
            onDownloadFormat={handleDownloadFormat}
            onImportTowers={handleImportTowers}
            onExportTowers={handleExportTowers}
            selectedActivities={selectedActivitiesForTable}
          />
        </Grid>
      )}

      <ModalComponent
        open={mostrarModal}
        onClose={cerrarModal}
        message={showModal.message}
      />
    </Box>
  );
};

export default Proyectos;